// How a station installs an app for players, and where the app keeps a player's sign-in (F-CAT-09, ADR-0045; spec/14 §6).
//
// Some apps install per Windows account (Discord, Spotify) or are registered per account (Store apps such as WhatsApp),
// so a station's player account never has them. HQ — and only HQ — writes the recipe a station uses to install one
// into its player account when a venue turns "Install for players" on:
//
//   None             stations never install it (the default)
//   Microsoft Store  the package family name; a package already on the PC is registered for the player
//   Vendor installer the vendor's own HTTPS download link, the hosts it may come from (redirects too), who must have
//                    signed the file, the silent arguments, the per-user file that proves it installed, the processes to
//                    close if the installer starts the app (never one of the PC's own), the Run values to remove so it never
//                    starts by itself, how long the app may run first to finish its own downloads, and the app's install
//                    folder — which PCs check and put back after every session, so no player can leave changed code for the
//                    next — with the data inside it that is never put back
//
// Sign-out paths are folders and files under the player's %LocalAppData% or %AppData% that a session end clears, so the
// next player is signed out. The station keeps its own list for the common launchers; these add to it.
//
// The drafts hold every field as typed; `toProvisioning` turns them into the contract's shape and says what is missing
// before anything is sent (the server holds the same rules and answers 400 otherwise).

import type { AppDataRoot, AppProvisioning, AppProvisioningKind, AppSignOutPath, Game, ProvisioningChildTool } from '@clavian/api-client';
import {
  httpsHostOf,
  INSTALL_ROOT_PATTERN,
  isBroadInstallRoot,
  isInsideInstallRoot,
  isProtectedProcessName,
  PER_USER_PATH_PATTERN,
  PROVISIONING_CHILD_TOOLS,
  RESTORE_EXCLUDE_PATTERN,
} from '@clavian/contracts/constants';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, SegmentedControl, SelectField, TextField } from '@clavian/ui-web';
import type { ReactNode } from 'react';

/** "None" or one of the recipe kinds. */
export type ProvisioningChoice = 'none' | AppProvisioningKind;

export interface ProvisioningDraft {
  choice: ProvisioningChoice;
  packageFamilyName: string;
  downloadUrl: string;
  /** Hosts as typed: separated by commas, spaces or lines. */
  allowedHosts: string;
  signer: string;
  silentArgs: string;
  installedPath: string;
  /** Process names as typed: separated by commas. */
  closeProcesses: string;
  /** Run value names as typed: separated by commas. */
  removeRunValues: string;
  /** Seconds as typed; blank is the station's default. */
  timeoutSeconds: string;
  /** The app's own install folder, as typed. */
  installRoot: string;
  /** Data paths inside the install folder, as typed: separated by commas. */
  restoreExclude: string;
  /** Seconds as typed; blank is none. */
  firstRunSeconds: string;
  /** The always-blocked tools the installed app itself starts in a session, recorded quietly when it is the verified app. */
  expectedBlockedChildren: readonly ProvisioningChildTool[];
}

export interface SignOutDraft {
  root: AppDataRoot;
  path: string;
  kind: 'directory' | 'file';
}

const list = (text: string, separators: RegExp): string[] =>
  text
    .split(separators)
    .map((value) => value.trim())
    .filter((value) => value !== '');

export function provisioningDraftOf(app: Pick<Game, 'provisioning'> | undefined): ProvisioningDraft {
  const recipe = app?.provisioning ?? null;
  return {
    choice: recipe === null ? 'none' : recipe.kind,
    packageFamilyName: recipe?.packageFamilyName ?? '',
    downloadUrl: recipe?.downloadUrl ?? '',
    allowedHosts: (recipe?.allowedHosts ?? []).join(', '),
    signer: recipe?.signer ?? '',
    silentArgs: recipe?.silentArgs ?? '',
    installedPath: recipe?.installedPath ?? '',
    closeProcesses: (recipe?.closeProcesses ?? []).join(', '),
    removeRunValues: (recipe?.removeRunValues ?? []).join(', '),
    timeoutSeconds: recipe?.timeoutSeconds === undefined ? '' : String(recipe.timeoutSeconds),
    installRoot: recipe?.installRoot ?? '',
    restoreExclude: (recipe?.restoreExclude ?? []).join(', '),
    firstRunSeconds: recipe?.firstRunSeconds === undefined ? '' : String(recipe.firstRunSeconds),
    expectedBlockedChildren: recipe?.expectedBlockedChildren ?? [],
  };
}

export function signOutDraftsOf(app: Pick<Game, 'signOutPaths'> | undefined): SignOutDraft[] {
  return (app?.signOutPaths ?? []).map((path) => ({ root: path.root, path: path.path, kind: path.kind }));
}

/** What `toProvisioning` found wrong, as a copy key under `catalogue.apps.players.problem`. */
export type ProvisioningProblem =
  | 'packageFamilyName'
  | 'downloadUrl'
  | 'notHttps'
  | 'allowedHosts'
  | 'hostNotAllowed'
  | 'signer'
  | 'installedPath'
  | 'perUserPath'
  | 'timeout'
  | 'protectedProcess'
  | 'installRoot'
  | 'broadInstallRoot'
  | 'outsideInstallRoot'
  | 'restoreExclude'
  | 'firstRun'
  | 'signOutPath';

const RELATIVE = /^(?![\\/])(?!.*(?:^|[\\/])\.\.?(?:[\\/]|$))[^%{}:*?"<>|]+$/;

/** The draft as the contract's recipe (`null` for None), or the first problem with it. Pure. */
export function toProvisioning(draft: ProvisioningDraft): { readonly ok: true; readonly provisioning: AppProvisioning | null } | { readonly ok: false; readonly problem: ProvisioningProblem } {
  if (draft.choice === 'none') return { ok: true, provisioning: null };
  if (draft.choice === 'store') {
    const family = draft.packageFamilyName.trim();
    if (!/^[A-Za-z0-9][A-Za-z0-9.-]{1,49}_[a-z0-9]{13}$/.test(family)) return { ok: false, problem: 'packageFamilyName' };
    return { ok: true, provisioning: { kind: 'store', packageFamilyName: family } };
  }
  const downloadUrl = draft.downloadUrl.trim();
  if (downloadUrl === '') return { ok: false, problem: 'downloadUrl' };
  const host = httpsHostOf(downloadUrl);
  if (host === undefined) return { ok: false, problem: 'notHttps' };
  const hosts = list(draft.allowedHosts.toLowerCase(), /[\s,]+/);
  if (hosts.length === 0) return { ok: false, problem: 'allowedHosts' };
  if (!hosts.includes(host)) return { ok: false, problem: 'hostNotAllowed' };
  const signer = draft.signer.trim();
  if (signer === '') return { ok: false, problem: 'signer' };
  const installedPath = draft.installedPath.trim();
  if (installedPath === '') return { ok: false, problem: 'installedPath' };
  if (!PER_USER_PATH_PATTERN.test(installedPath)) return { ok: false, problem: 'perUserPath' };
  const installRoot = draft.installRoot.trim().replace(/\\+$/, '');
  if (!INSTALL_ROOT_PATTERN.test(installRoot)) return { ok: false, problem: 'installRoot' };
  if (isBroadInstallRoot(installRoot)) return { ok: false, problem: 'broadInstallRoot' };
  if (!isInsideInstallRoot(installedPath, installRoot)) return { ok: false, problem: 'outsideInstallRoot' };
  const restoreExclude = list(draft.restoreExclude, /,/);
  if (restoreExclude.some((path) => !RESTORE_EXCLUDE_PATTERN.test(path))) return { ok: false, problem: 'restoreExclude' };
  const timeout = draft.timeoutSeconds.trim();
  const seconds = timeout === '' ? undefined : Number(timeout);
  if (seconds !== undefined && (!Number.isInteger(seconds) || seconds < 30 || seconds > 1800)) return { ok: false, problem: 'timeout' };
  const firstRun = draft.firstRunSeconds.trim();
  const firstRunSeconds = firstRun === '' ? undefined : Number(firstRun);
  if (firstRunSeconds !== undefined && (!Number.isInteger(firstRunSeconds) || firstRunSeconds < 0 || firstRunSeconds > 600)) return { ok: false, problem: 'firstRun' };
  const closeProcesses = list(draft.closeProcesses, /,/);
  if (closeProcesses.some(isProtectedProcessName)) return { ok: false, problem: 'protectedProcess' };
  const removeRunValues = list(draft.removeRunValues, /,/);
  // Only the tools the contract allows, in its order, once each.
  const expectedBlockedChildren = PROVISIONING_CHILD_TOOLS.filter((tool) => draft.expectedBlockedChildren.includes(tool));
  return {
    ok: true,
    provisioning: {
      kind: 'installer',
      downloadUrl,
      allowedHosts: hosts,
      signer,
      ...(draft.silentArgs.trim() === '' ? {} : { silentArgs: draft.silentArgs.trim() }),
      installedPath,
      ...(closeProcesses.length === 0 ? {} : { closeProcesses }),
      ...(removeRunValues.length === 0 ? {} : { removeRunValues }),
      ...(seconds === undefined ? {} : { timeoutSeconds: seconds }),
      installRoot,
      ...(restoreExclude.length === 0 ? {} : { restoreExclude }),
      ...(firstRunSeconds === undefined ? {} : { firstRunSeconds }),
      ...(expectedBlockedChildren.length === 0 ? {} : { expectedBlockedChildren }),
    },
  };
}

/** The sign-out drafts as the contract's paths (blank rows dropped), or a problem when one leaves its folder. Pure. */
export function toSignOutPaths(drafts: readonly SignOutDraft[]): { readonly ok: true; readonly paths: AppSignOutPath[] } | { readonly ok: false; readonly problem: 'signOutPath' } {
  const paths: AppSignOutPath[] = [];
  for (const draft of drafts) {
    const path = draft.path.trim();
    if (path === '') continue;
    if (!RELATIVE.test(path)) return { ok: false, problem: 'signOutPath' };
    paths.push({ root: draft.root, path, kind: draft.kind });
  }
  return { ok: true, paths };
}

/** The "For players" step: the recipe, then the sign-out paths. */
export function ProvisioningFields({
  draft,
  onChange,
  signOut,
  onSignOutChange,
}: {
  readonly draft: ProvisioningDraft;
  readonly onChange: (draft: ProvisioningDraft) => void;
  readonly signOut: readonly SignOutDraft[];
  readonly onSignOutChange: (drafts: SignOutDraft[]) => void;
}): ReactNode {
  const { t } = useTranslation('hq');
  const set = (patch: Partial<ProvisioningDraft>) => onChange({ ...draft, ...patch });
  const setRow = (index: number, patch: Partial<SignOutDraft>) => onSignOutChange(signOut.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <>
      <p className="console-muted">{t('catalogue.apps.players.intro')}</p>
      <SegmentedControl<ProvisioningChoice>
        label={t('catalogue.apps.players.kind')}
        options={[
          { value: 'none', label: t('catalogue.apps.players.kindNone') },
          { value: 'store', label: t('catalogue.apps.players.kindStore') },
          { value: 'installer', label: t('catalogue.apps.players.kindInstaller') },
        ]}
        value={draft.choice}
        onValueChange={(choice) => set({ choice })}
      />

      {draft.choice === 'none' ? <p className="console-muted">{t('catalogue.apps.players.noneHint')}</p> : null}

      {draft.choice === 'store' ? (
        <TextField
          label={t('catalogue.apps.players.packageFamilyName')}
          description={t('catalogue.apps.players.packageFamilyNameHint')}
          required
          value={draft.packageFamilyName}
          onChange={(event) => set({ packageFamilyName: event.target.value })}
        />
      ) : null}

      {draft.choice === 'installer' ? (
        <>
          <TextField label={t('catalogue.apps.players.downloadUrl')} description={t('catalogue.apps.players.downloadUrlHint')} required value={draft.downloadUrl} onChange={(event) => set({ downloadUrl: event.target.value })} />
          <TextField label={t('catalogue.apps.players.allowedHosts')} description={t('catalogue.apps.players.allowedHostsHint')} required value={draft.allowedHosts} onChange={(event) => set({ allowedHosts: event.target.value })} />
          <TextField label={t('catalogue.apps.players.signer')} description={t('catalogue.apps.players.signerHint')} required value={draft.signer} onChange={(event) => set({ signer: event.target.value })} />
          <div className="console-form__row">
            <TextField label={t('catalogue.apps.players.silentArgs')} description={t('catalogue.apps.players.silentArgsHint')} showOptional value={draft.silentArgs} onChange={(event) => set({ silentArgs: event.target.value })} />
            <TextField
              label={t('catalogue.apps.players.timeoutSeconds')}
              description={t('catalogue.apps.players.timeoutSecondsHint')}
              showOptional
              numeric
              inputMode="numeric"
              value={draft.timeoutSeconds}
              onChange={(event) => set({ timeoutSeconds: event.target.value })}
            />
          </div>
          <TextField label={t('catalogue.apps.players.installRoot')} description={t('catalogue.apps.players.installRootHint')} required value={draft.installRoot} onChange={(event) => set({ installRoot: event.target.value })} />
          <TextField label={t('catalogue.apps.players.installedPath')} description={t('catalogue.apps.players.installedPathHint')} required value={draft.installedPath} onChange={(event) => set({ installedPath: event.target.value })} />
          <div className="console-form__row">
            <TextField label={t('catalogue.apps.players.restoreExclude')} description={t('catalogue.apps.players.restoreExcludeHint')} showOptional value={draft.restoreExclude} onChange={(event) => set({ restoreExclude: event.target.value })} />
            <TextField
              label={t('catalogue.apps.players.firstRunSeconds')}
              description={t('catalogue.apps.players.firstRunSecondsHint')}
              showOptional
              numeric
              inputMode="numeric"
              value={draft.firstRunSeconds}
              onChange={(event) => set({ firstRunSeconds: event.target.value })}
            />
          </div>
          <div className="console-form__row">
            <TextField label={t('catalogue.apps.players.closeProcesses')} description={t('catalogue.apps.players.closeProcessesHint')} showOptional value={draft.closeProcesses} onChange={(event) => set({ closeProcesses: event.target.value })} />
            <TextField label={t('catalogue.apps.players.removeRunValues')} description={t('catalogue.apps.players.removeRunValuesHint')} showOptional value={draft.removeRunValues} onChange={(event) => set({ removeRunValues: event.target.value })} />
          </div>
          <fieldset className="console-hq-fieldset">
            <legend>{t('catalogue.apps.players.expectedBlockedChildren')}</legend>
            <p className="console-muted">{t('catalogue.apps.players.expectedBlockedChildrenHint')}</p>
            {PROVISIONING_CHILD_TOOLS.map((tool) => (
              <Checkbox
                key={tool}
                label={t(`catalogue.apps.players.expectedChildTool.${tool}`)}
                checked={draft.expectedBlockedChildren.includes(tool)}
                onCheckedChange={(checked) =>
                  set({
                    expectedBlockedChildren:
                      checked === true ? [...draft.expectedBlockedChildren.filter((held) => held !== tool), tool] : draft.expectedBlockedChildren.filter((held) => held !== tool),
                  })
                }
              />
            ))}
          </fieldset>
        </>
      ) : null}

      <fieldset className="console-hq-fieldset">
        <legend>{t('catalogue.apps.players.signOut')}</legend>
        <p className="console-muted">{t('catalogue.apps.players.signOutHint')}</p>
        {signOut.map((row, index) => (
          <div key={index} className="console-form__row console-sign-out-row">
            <SelectField
              label={t('catalogue.apps.players.signOutRoot')}
              options={[
                { value: 'local_app_data', label: t('catalogue.apps.players.root.local_app_data') },
                { value: 'app_data', label: t('catalogue.apps.players.root.app_data') },
              ]}
              value={row.root}
              onValueChange={(root) => setRow(index, { root: root as AppDataRoot })}
            />
            <TextField label={t('catalogue.apps.players.signOutPath')} value={row.path} onChange={(event) => setRow(index, { path: event.target.value })} />
            <SelectField
              label={t('catalogue.apps.players.signOutKind')}
              options={[
                { value: 'directory', label: t('catalogue.apps.players.kindDirectory') },
                { value: 'file', label: t('catalogue.apps.players.kindFile') },
              ]}
              value={row.kind}
              onValueChange={(kind) => setRow(index, { kind: kind as SignOutDraft['kind'] })}
            />
            <Button type="button" variant="ghost" size="sm" onClick={() => onSignOutChange(signOut.filter((_, i) => i !== index))}>
              {t('catalogue.apps.players.signOutRemove')}
            </Button>
          </div>
        ))}
        <div className="console-form__actions">
          <Button type="button" variant="secondary" size="sm" onClick={() => onSignOutChange([...signOut, { root: 'app_data', path: '', kind: 'directory' }])}>
            {t('catalogue.apps.players.signOutAdd')}
          </Button>
        </div>
      </fieldset>
    </>
  );
}
